/**
 * @file GridModel.js
 * @module GridModel
 *
 * This module provides classes and utilities for representing and manipulating a crossword-style grid model.
 * It includes the main `GridModel` class for managing the grid state, `GridTile` for individual cell representation,
 * and `GridModelWord` for representing words (across and down) within the grid. The model supports solution, playing,
 * peeking, and scratch values for each tile, and provides methods for querying and updating the grid.
 *
 * @author alex
 */

import { GridTile } from "./GridTile.js";
import { GridModelWord } from "./GridModelWord.js";
// import { FinModel } from './FinModel.js';

// Grid layer constants
export const GRID_LAYER_SOLUTION = "solution";
export const GRID_LAYER_SCRATCH = "scratch";
export const GRID_LAYER_PLAYING = "playing";
export const GRID_LAYER_PEEKING = "peeking";

// Optionally, group them in an object for convenience
export const GRID_LAYERS = {
  SOLUTION: GRID_LAYER_SOLUTION,
  SCRATCH: GRID_LAYER_SCRATCH,
  PLAYING: GRID_LAYER_PLAYING,
  PEEKING: GRID_LAYER_PEEKING,
};

export const ORIENTATION_ACROSS = "ACROSS";
export const ORIENTATION_DOWN = "DOWN";

export const ORIENTATION = {
  ACROSS: ORIENTATION_ACROSS,
  DOWN: ORIENTATION_DOWN,
};

/**
 * Represents a crossword-style grid model, managing the placement and state of words and tiles.
 * Provides methods for accessing and manipulating the grid, including solution, playing, peeking, and scratch values.
 * Supports both across and down word orientations.
 *
 * @class
 */
export class GridModel {
  /**
   * @param {FinModel} finModel - FinModel object.
   */
  constructor(finModel) {
    this.grid = null; // 2D grid (Array of Arrays)
    this.rowCount = 0; // this is 0's based
    this.colCount = 0; // this is 0's based
    this.startWordNumber = 0;
    this.startWord = null;
    this.startGridWord = null;
    this.acrossGridWords = []; // Array to hold across gridwords
    this.downGridWords = []; // Array to hold down gridwords
    this.gridModelWords = []; // Array to hold all gridwords
    this.currentLayer = GRID_LAYERS.PLAYING; // This layer does all the work

    this.currentSelection = null;
    this.currentOrientation = ORIENTATION.ACROSS;
    this.parseFinModel(finModel);
  }

  /**
   * Parse the FinModel and initialize the grid with words and tiles.
   * Creates the 2D grid array of GridTile objects and places all across and down words.
   *
   * @param {FinModel} finModel - The puzzle data model containing word definitions and grid dimensions
   * @returns {void}
   */
  parseFinModel(finModel) {
    let acrossWords = finModel.getAcrossWords() || [];
    let downWords = finModel.getDownWords() || [];
    this.rowCount = finModel.getRowCount() || 0; // Get row count from FinModel
    this.colCount = finModel.getColumnCount() || 0; // Get column count from FinModel
    this.startWordNumber = finModel.getStartingWordNumber() || 0;
    this.startWord = finModel.getStartingWord();

    // Initialize empty grid with nulls
    this.grid = Array.from({ length: this.rowCount }, () =>
      Array(this.colCount).fill(null),
    );
    this.acrossGridWords = this.placeAcrossWords(acrossWords);
    if (this.verifyDownWords(downWords)) {
      this.downGridWords = this.placeDownWords(downWords);
    } else {
      console.log("Down and Across words mismatch, down words not placed");
      throw new Error("GridModel verify down words: mismatch across words.");
    }
    this.gridModelWords = [...this.acrossGridWords, ...this.downGridWords];
    this.currentSelection = { row: 0, col: 0 };
  }

  /**
   * Get the starting word for the puzzle based on the FinModel's starting word.
   * Falls back to first across word, then first down word if no starting word specified.
   *
   * @returns {GridModelWord|null} The starting grid word or null if no words available
   */
  getStartGridWord() {
    // Set the starting grid word based on the starting word in the finModel
    const startWord = this.startWord;
    if (window.CLUEBOARD_DEBUG_LOG)
      console.log("GridModel: starting word:", startWord);
    if (startWord) {
      // Try to find the matching GridModelWord in across or down
      let found = this.acrossGridWords.find(
        (w) => w.getWordValue() === startWord.wordvalue,
      );
      if (!found) {
        found = this.downGridWords.find(
          (w) => w.getWordValue() === startWord.wordvalue,
        );
      }
      this.startingGridWord = found || null;
    } else if (this.acrossGridWords.length > 0) {
      this.startingGridWord = this.acrossGridWords[0];
    } else if (this.downGridWords.length > 0) {
      this.startingGridWord = this.downGridWords[0];
    } else {
      this.startingGridWord = null;
    }

    return this.startingGridWord;
  }

  /**
   * Get the current word orientation (ACROSS or DOWN).
   *
   * @returns {ORIENTATION} The current orientation constant
   */
  getCurrentOrientation() {
    return this.currentOrientation;
  }

  /**
   * Get a copy of the current tile selection coordinates.
   *
   * @returns {Object} Object with row and col properties (0-based indices)
   */
  getCurrentSelection() {
    return { ...this.currentSelection };
  }

  /**
   * Set the current tile selection to the specified coordinates.
   *
   * @param {number} row - 0-based row index
   * @param {number} col - 0-based column index
   * @returns {void}
   */
  setCurrentSelection(row, col) {
    this.currentSelection = {
      row: row,
      col: col,
    };
  }

  /**
   * Switch the current orientation between ACROSS and DOWN.
   */
  switchOrientation() {
    this.currentOrientation =
      this.currentOrientation === ORIENTATION.ACROSS
        ? ORIENTATION.DOWN
        : ORIENTATION.ACROSS;
  }

  /**
   * Set the selection to the given word and select the first empty tile in that word.
   * @param {GridModelWord} word - The word to select.
   */
  setSelectionToWord(word) {
    if (!word || typeof word.getTiles !== "function") return;
    const tiles = word.getTiles();
    let firstEmptyTile = tiles.find((tile) => {
      const val = tile.getPlayingValue ? tile.getPlayingValue() : "";
      return !val || val === " ";
    });
    if (!firstEmptyTile) {
      firstEmptyTile = tiles[0];
    }
    if (firstEmptyTile) {
      this.currentSelection = {
        row: firstEmptyTile.row,
        col: firstEmptyTile.col,
      };
      this.currentOrientation = word.orientation;
    }
  }

  /**
   * Place across words into the grid matrix.
   * Each word object should have: row, col, wordvalue, and length.
   * @param {Array<Object>} acrossWords - list of across grid model words
   */
  placeAcrossWords(acrossWords) {
    if (!Array.isArray(acrossWords)) return;

    let gridModelWord = null;
    let gridWordList = [];

    for (const word of acrossWords) {
      const row = word.row - 1;
      const col = word.column - 1;
      const value = word.wordvalue;
      if (
        typeof row === "number" &&
        typeof col === "number" &&
        typeof value === "string"
      ) {
        let wordTiles = [];
        for (let i = 0; i < value.length; i++) {
          if (
            row >= 0 &&
            row < this.rowCount &&
            col + i >= 0 &&
            col + i < this.colCount
          ) {
            const gridTile = new GridTile(value[i], row, col + i);
            this.grid[row][col + i] = gridTile; // Place the letter in the grid
            wordTiles.push(gridTile);
          }
        }
        // Create a GridModelWord object for the across word
        if (wordTiles.length > 0) {
          gridModelWord = new GridModelWord(wordTiles, "ACROSS", row, col);
          // You can store or use gridModelWord as needed
          gridWordList.push(gridModelWord);
        }
        // Set the acrossGridWord property on each tile
        wordTiles.forEach((tile) => {
          tile.acrossGridWord = gridModelWord;
        });
      }
    }
    return gridWordList;
  }

  /**
   * Place down words into the grid matrix.
   * Each word object should have: row, col, wordvalue, and length.
   * Returns a list of GridModelWord objects for all down words.
   */
  placeDownWords(downWords) {
    if (!Array.isArray(downWords)) return;

    let gridModelWord = null;
    let gridWordList = [];

    for (const word of downWords) {
      const row = word.row - 1;
      const col = word.column - 1;
      const value = word.wordvalue;
      if (
        typeof row === "number" &&
        typeof col === "number" &&
        typeof value === "string"
      ) {
        let wordTiles = [];
        for (let i = 0; i < value.length; i++) {
          if (
            row + i >= 0 &&
            row + i < this.rowCount &&
            col >= 0 &&
            col < this.colCount
          ) {
            // Only create a new GridTile if one doesn't exist at this position
            if (!this.grid[row + i][col]) {
              console.log("FinModel Error: missing tile");
              throw new Error("GridModel down words: missing tile.");
            } else {
              wordTiles.push(this.grid[row + i][col]);
            }
          }
        }
        // Create a GridModelWord object for the down word
        if (wordTiles.length > 0) {
          gridModelWord = new GridModelWord(wordTiles, "DOWN", row, col);
          gridWordList.push(gridModelWord);
        }
        // Set the downridModelWord property on each tile
        wordTiles.forEach((tile) => {
          tile.downGridWord = gridModelWord;
        });
      }
    }
    return gridWordList;
  }

  /**
   * Verify that down words don't conflict with already placed across words.
   * Checks each letter position for consistency between intersecting words.
   *
   * @param {Array<Object>} downWords - Array of down word objects from FinModel
   * @returns {boolean} True if all down words can be placed without conflicts
   */
  verifyDownWords(downWords) {
    if (!Array.isArray(downWords)) return false;
    for (const word of downWords) {
      const row = word.row - 1;
      const col = word.column - 1;
      const value = word.wordvalue;
      if (
        typeof row === "number" &&
        typeof col === "number" &&
        typeof value === "string"
      ) {
        for (let i = 0; i < value.length; i++) {
          if (
            row + i >= 0 &&
            row + i < this.rowCount &&
            col >= 0 &&
            col < this.colCount
          ) {
            // const gridTile = this.grid[row + i][col];
            if (
              this.grid[row + i][col] !== null &&
              this.grid[row + i][col] !== value[i]
            ) {
              const gridTile = this.grid[row + i][col];
              if (gridTile.solution !== value[i]) {
                return false; // Conflict found
              }
            }
          }
        }
      }
    }
    return true; // All down words placed without conflict
  }

  /**
   * Update the current selection based on keyboard arrow key events.
   * @param {KeyboardEvent} event - The keyboard event object.
   */
  update(event) {
    if (!this.currentSelection) return;
    let { row, col } = this.currentSelection;
    let newRow = row;
    let newCol = col;

    switch (event.key) {
      case "ArrowUp":
        if (this.currentOrientation === ORIENTATION.ACROSS) {
          this.switchOrientation();
          return;
        }
        do {
          newRow--;
        } while (newRow >= 0 && !this.grid[newRow][col]);
        break;
      case "ArrowDown":
        if (this.currentOrientation === ORIENTATION.ACROSS) {
          this.switchOrientation();
          return;
        }
        do {
          newRow++;
        } while (newRow < this.rowCount && !this.grid[newRow][col]);
        break;
      case "ArrowLeft":
        if (this.currentOrientation === ORIENTATION.DOWN) {
          this.switchOrientation();
          return;
        }
        do {
          newCol--;
        } while (newCol >= 0 && !this.grid[row][newCol]);
        break;
      case "ArrowRight":
        if (this.currentOrientation === ORIENTATION.DOWN) {
          this.switchOrientation();
          return;
        }
        do {
          newCol++;
        } while (newCol < this.colCount && !this.grid[row][newCol]);
        break;
      default:
        return;
    }

    // Ensure new position is within bounds and not a black tile
    if (
      newRow >= 0 &&
      newRow < this.rowCount &&
      newCol >= 0 &&
      newCol < this.colCount &&
      this.grid[newRow][newCol]
    ) {
      this.setCurrentSelection(newRow, newCol);
    }
  }

  /**
   * Get the grid matrix.
   * @returns {Array<Array<string|null>>}
   */
  getGrid() {
    return this.grid;
  }
  /**
   * Get the number of rows in the grid.
   * @returns {number}
   */
  getRowCount() {
    return this.rowCount;
  }
  /**
   * Get the number of columns in the grid.
   * @returns {number}
   */
  getColumnCount() {
    return this.colCount;
  }
  /**
   * Get all across words in the grid.
   * @returns {GridModelWord[]}
   */
  getAcrossGridWords() {
    return this.acrossGridWords;
  }
  /**
   * Get all down words in the grid.
   * @returns {GridModelWord[]}
   */
  getDownGridWords() {
    return this.downGridWords;
  }
  /**
   * Get all words in the grid (both across and down).
   * @returns {GridModelWord[]}
   */
  getAllWords() {
    return this.gridModelWords;
  }

  /**
   * Get a GridModelWord by its word value (the actual word string).
   * Searches both across and down words.
   * @param {string} wordValue - The word value to search for.
   * @param {ORIENTATION} [orientation] - Optional: ORIENTATION.ACROSS or ORIENTATION.DOWN. If not provided, searches both.
   * @returns {GridModelWord|null}
   */
  getGridWordByValue(wordValue, orientation = null) {
    if (orientation === ORIENTATION.ACROSS) {
      return (
        this.acrossGridWords.find((w) => w.getWordValue() === wordValue) || null
      );
    } else if (orientation === ORIENTATION.DOWN) {
      return (
        this.downGridWords.find((w) => w.getWordValue() === wordValue) || null
      );
    } else {
      // Search both if no orientation specified
      return (
        this.acrossGridWords.find((w) => w.getWordValue() === wordValue) ||
        this.downGridWords.find((w) => w.getWordValue() === wordValue) ||
        null
      );
    }
  }

  /**
   * Get a GridModelWord by its guess value (the current user input).
   * Searches both across and down words by comparing their current guesses.
   * @param {string} guessValue - The guess value to search for.
   * @param {ORIENTATION} [orientation] - Optional: ORIENTATION.ACROSS or ORIENTATION.DOWN. If not provided, searches both.
   * @returns {GridModelWord|null}
   */
  getGridWordByGuessValue(guessValue, orientation = null) {
    if (orientation === ORIENTATION.ACROSS) {
      return (
        this.acrossGridWords.find((w) => w.getGuessValue() === guessValue) ||
        null
      );
    } else if (orientation === ORIENTATION.DOWN) {
      return (
        this.downGridWords.find((w) => w.getGuessValue() === guessValue) || null
      );
    } else {
      // Search both if no orientation specified
      return (
        this.acrossGridWords.find((w) => w.getGuessValue() === guessValue) ||
        this.downGridWords.find((w) => w.getGuessValue() === guessValue) ||
        null
      );
    }
  }

  /**
   *  Get a word dictionary
   * @returns {Dictionary<string, GridModelWord>}
   */
  getWordDictionary() {
    // Create a dictionary of words for easy access
    let wordDictionary = {};
    for (const word of [...this.acrossGridWords, ...this.downGridWords]) {
      wordDictionary[word.getWordValue()] = word;
    }
    return wordDictionary;
  }

  /**
   * Get the across word at the specified grid position.
   *
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {GridModelWord|null} The across word at this position or null
   * @throws {Error} If row or column parameters are invalid or out of bounds
   */
  getAcrossGridWordAt(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getAcrossGridWordAt: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getAcrossGridWordAt: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.acrossGridWord : null;
  }
  /**
   * Get the down word at the specified grid position.
   *
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {GridModelWord|null} The down word at this position or null
   * @throws {Error} If row or column parameters are invalid or out of bounds
   */
  getDownGridWordAt(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getDownGridWordAt: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getDownGridWordAt: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.downGridWord : null;
  }

  /**
   * Get a specific tile in the grid by row, column.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {GridTile|null} - Returns the GridTile at the specified position or null if not found
   */
  getTile(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      return null;
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      return null;
    }
    return this.grid[row][column] || null;
  }
  /**
   * Get a specific tile in the grid by location.
   * @param {object} location - 0-based row, col tuple
   * @returns {GridTile|null} - Returns the GridTile at the specified position or null if not found
   */
  getTileAtLocation(location) {
    return this.getTile(location.row, location.col);
  }

  /**
   * Get the currently selected tile.
   * @returns {GridTile|null} - The tile at the current selection or null if not found.
   */
  getSelectedTile() {
    if (!this.currentSelection) return null;
    const { row, col } = this.currentSelection;
    return this.getTile(row, col);
  }

  /**
   * Reveal the solution for the currently selected tile.
   *
   * @returns {void}
   */
  solveSelectedTile() {
    const tile = this.getSelectedTile();
    this.solveTile(tile);
  }

  /**
   * Reveal the solution for a specific tile by setting its revealed character.
   *
   * @param {GridTile} tile - The tile to solve
   * @returns {void}
   */
  solveTile(tile) {
    const solution = this.getSolutionForTile(tile.row, tile.col);
    tile.setRevealedChar(solution);
  }

  /**
   * Get the currently selected word based on current selection and orientation.
   * @returns {GridModelWord|null}
   */
  getSelectedWord() {
    if (!this.currentSelection) return null;
    const { row, col } = this.currentSelection;
    if (this.currentOrientation === ORIENTATION.ACROSS) {
      return this.getAcrossGridWordAt(row, col);
    } else if (this.currentOrientation === ORIENTATION.DOWN) {
      return this.getDownGridWordAt(row, col);
    }
    return null;
  }

  /**
   * Solve the currently selected word if there is a selection.
   */
  solveSelectedWord() {
    const word = this.getSelectedWord();
    this.solveWord(word);
  }

  /**
   * Clear the currently selected word if there is a selection.
   */
  clearSelectedWord() {
    console.log("Clear Selected Word. Yes!");
    const word = this.getSelectedWord();
    this.clearWord(word);
  }

  /**
   * Solve the word by copying the solution value into the playing value for each tile in the word.
   * @param {GridModelWord} word - The word to select.
   */
  solveWord(word) {
    if (!word) return;
    const tiles = word.getTiles ? word.getTiles() : word.tiles;
    if (!Array.isArray(tiles)) return;
    tiles.forEach((tile) => {
      this.solveTile(tile);
    });
  }

  /**
   * Clear the word by setting the playing value to a blank
   * @param {GridModelWord} word - The word to select.
   */
  clearWord(word) {
    if (!word) return;
    const tiles = word.getTiles ? word.getTiles() : word.tiles;
    if (!Array.isArray(tiles)) return;
    tiles.forEach((tile) => {
      if (tile && typeof tile.removeGuessChar === "function") {
        tile.removeGuessChar();
      }
    });
  }

  /**
   * Get the solution for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {string|null} - Returns the solution value of the tile at the specified position or null if not found
   */
  getSolutionForTile(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getSolutionForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getSolutionForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.getSolutionValue() : null;
  }
  /**
   * Get the playing value for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {string|null} - Returns the playing value of the tile at the specified position or null if not found
   */
  getPlayingForTile(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getPlayingForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getPlayingForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.getPlayingValue() : null;
  }
  /**
   * Set the playing value for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @param {string} value - The value to set for the tile
   */
  setPlayingForTile(row, column, value) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel setPlayingForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel setPlayingForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    if (tile) {
      tile.setPlayingValue(value);
    } else {
      throw new Error(
        `GridModel setPlayingForTile: No tile found at (${row}, ${column}).`,
      );
    }
  }
  /**
   * Get the peeking value for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {string|null} - Returns the peeking value of the tile at the specified position or null if not found
   */
  getPeekingForTile(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getPeekingForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getPeekingForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.getPeekingValue() : null;
  }
  /**
   * Get the scratch value for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {string|null} - Returns the scratch value of the tile at the specified position or null if not found
   */
  getScratchForTile(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel getScratchForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel getScratchForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? tile.getScratchValue() : null;
  }
  /**
   * Set the scratch value for a specific tile in the grid.
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @param {string} value - The value to set for the tile
   */
  setScratchForTile(row, column, value) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel setScratchForTile: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel setScratchForTile: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    if (tile) {
      tile.scratch = value;
    } else {
      throw new Error(
        `GridModel setScratchForTile: No tile found at (${row}, ${column}).`,
      );
    }
  }
  /**     * Check if a specific tile is black (not playable).
   * @param {number} row - 0-based row index
   * @param {number} column - 0-based column index
   * @returns {boolean} - Returns true if the tile is black, false otherwise
   */
  isTileBlack(row, column) {
    if (
      typeof row !== "number" ||
      isNaN(row) ||
      row < 0 ||
      row >= this.rowCount
    ) {
      throw new Error(
        "GridModel isTileBlack: 'row' must be a valid 1-based number within grid bounds.",
      );
    }
    if (
      typeof column !== "number" ||
      isNaN(column) ||
      column < 0 ||
      column >= this.colCount
    ) {
      throw new Error(
        "GridModel isTileBlack: 'column' must be a valid 1-based number within grid bounds.",
      );
    }
    const tile = this.grid[row][column]; // Convert to 0-based index
    return tile ? true : false;
  }
  /**     * Get the solution for the entire grid.
   * @returns {Array<Array<Object|null>>} - Returns a 2D array of solution values
   */
  getSolutionGrid() {
    return this.grid.map((rowArr) =>
      rowArr.map((tile) => (tile ? tile : null)),
    );
  }
  /**     * Get the playing values for the entire grid.
   * @returns {Array<Array<string|null>>} - Returns a 2D array of playing values
   */
  getPlayingGrid() {
    return this.grid.map((rowArr) =>
      rowArr.map((tile) => (tile ? tile : null)),
    );
  }
  /**     * Get the peeking values for the entire grid.
   * @returns {Array<Array<string|null>>} - Returns a 2D array of peeking values
   */
  getPeekingGrid() {
    return this.grid.map((rowArr) =>
      rowArr.map((tile) => (tile ? tile : null)),
    );
  }
  /**     * Get the scratch values for the entire grid.
   * @returns {Array<Array<string|null>>} - Returns a 2D array of scratch values
   */
  getScratchGrid() {
    return this.grid.map((rowArr) =>
      rowArr.map((tile) => (tile ? tile : null)),
    );
  }
  /**
   * Get the grid matrix for rendering.
   * @returns {Array<Array<Object|null>>}
   *
   * This should return objects of another type which contain data
   * more relevant to the game view for drawing the puzzle grid. So
   * the objects would only contain a character value and positional
   * infromation and then lots of meta-data about how to draw the tile.
   */
  getMatrix() {
    const solution = this.getSolutionGrid();
    return solution;
  }

  /**
   * Get the current active grid layer (solution, playing, peeking, or scratch).
   *
   * @returns {string} The current layer constant from GRID_LAYERS
   */
  getCurrentLayer() {
    return this.currentLayer;
  }

  /**
   * Set the active grid layer for tile operations.
   *
   * @param {string} layer - Must be one of the GRID_LAYERS constants
   * @returns {void}
   * @throws {Error} If layer is not a valid GRID_LAYERS value
   */
  setCurrentLayer(layer) {
    if (Object.values(GRID_LAYERS).includes(layer)) {
      this.currentLayer = layer;
    } else {
      throw new Error(`GridModel setCurrentLayer: Invalid layer '${layer}'.`);
    }
  }

  /**
   * Select a word at the specified location and orientation.
   * Updates current selection and orientation if word is found.
   *
   * @param {ORIENTATION} orientation - ORIENTATION.ACROSS or ORIENTATION.DOWN
   * @param {Object} location - Object with row and col properties (0-based)
   * @returns {GridModelWord|null} The selected word or null if not found
   * @throws {Error} If location is invalid or orientation is not recognized
   */
  selectWordAtLocation(orientation, location) {
    if (
      !location ||
      typeof location.row !== "number" ||
      typeof location.col !== "number"
    ) {
      throw new Error(
        "GridModel selectWordAtLocation: location must have row and col properties.",
      );
    }

    let word = null;
    if (orientation === ORIENTATION.ACROSS) {
      word = this.getAcrossGridWordAt(location.row, location.col);
    } else if (orientation === ORIENTATION.DOWN) {
      word = this.getDownGridWordAt(location.row, location.col);
    } else {
      throw new Error("GridModel selectWordAtLocation: Invalid orientation.");
    }

    if (word) {
      // Optionally update selection and orientation
      this.currentSelection = { row: location.row, col: location.col };
      this.currentOrientation = orientation;
    }

    return word;
  }

  /**
   * Move the cursor to the next tile in the current word orientation.
   * Advances right for across words, down for down words.
   *
   * @returns {void}
   */
  advanceCursor() {
    let gridLoc = this.getCurrentSelection();
    const theOrientation = this.currentOrientation;

    if (theOrientation === ORIENTATION.ACROSS) {
      gridLoc.col++;
      if (this.getTileAtLocation(gridLoc)) {
        this.selectWordAtLocation(ORIENTATION.ACROSS, gridLoc);
      }
    } else {
      gridLoc.row++;
      if (this.getTileAtLocation(gridLoc)) {
        this.selectWordAtLocation(ORIENTATION.DOWN, gridLoc);
      }
    }
  }

  /**
   * Move the cursor back one tile and clear the current tile's playing value.
   * Moves left for across words, up for down words.
   *
   * @returns {void}
   */
  retreatCursor() {
    const { row, col } = this.getCurrentSelection();
    // Replace current character on playing layer with a space
    this.setPlayingForTile(row, col, " ");

    let newRow = row;
    let newCol = col;

    if (this.currentOrientation === ORIENTATION.ACROSS) {
      newCol--;
      while (newCol >= 0 && !this.getTile(newRow, newCol)) {
        newCol--;
      }
    } else if (this.currentOrientation === ORIENTATION.DOWN) {
      newRow--;
      while (newRow >= 0 && !this.getTile(newRow, newCol)) {
        newRow--;
      }
    }

    // Only update selection if within bounds and not a black tile
    if (
      newRow >= 0 &&
      newRow < this.rowCount &&
      newCol >= 0 &&
      newCol < this.colCount &&
      this.getTile(newRow, newCol)
    ) {
      this.setCurrentSelection(newRow, newCol);
    }
  }

  /**
   * Reset the grid to its initial state.
   */
  resetGrid() {
    this.grid.forEach((row) => {
      row.forEach((tile) => {
        if (tile) {
          tile.setPlayingValue("");
          tile.setPeekingValue("");
          tile.scratch = "";
        }
      });
    });
    this.currentLayer = GRID_LAYERS.PLAYING; // Reset to default layer
  }

  /**
   * Calculate progress based on number of filled tiles vs total playable tiles.
   * Does NOT check correctness—only if a tile is filled (not blank).
   * @returns {Object} - { filledTiles: number, totalTiles: number, percentage: number }
   */
  calculateProgress() {
    let filledTiles = 0;
    let totalTiles = 0;

    //FIXME: this is super wasteful done at each keystroke, cache some of these values
    this.grid.forEach((row) => {
      row.forEach((tile) => {
        if (tile) {
          // Only count non-null tiles (playable tiles)
          totalTiles++;
          const playingValue = tile.getPlayingValue();
          if (playingValue && playingValue !== GridTile.blankChar) {
            filledTiles++;
          }
        }
      });
    });

    const percentage =
      totalTiles > 0 ? Math.round((filledTiles / totalTiles) * 100) : 0;

    return {
      filledTiles,
      totalTiles,
      percentage,
    };
  }
}
